import { type RefObject, useEffect } from 'react';

import {
  WX_COPILOT_URL,
  WX_LAUNCH_DARKLY_CLIENT_SIDE_ID,
} from '@/config/runtime-env';
import { useAuthContext } from '@/contexts/auth-context';
import { useWxJwtReady } from '@/hooks/use-wx-jwt-ready';
import { domainMeta } from '@/utils/domain-meta';

type UseWxCopilotOptions = {
  mountRef?: RefObject<HTMLDivElement | null> | null;
  mountId?: string;
  renderProps?: Record<string, unknown>;
};

export default function useWxCopilot({
  mountRef = null,
  mountId = 'wx-copilot-root',
  renderProps = {},
}: UseWxCopilotOptions): void {
  const { whoami } = useAuthContext();
  const isJwtReady = useWxJwtReady();

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    // Don't initialize if no whoami data yet
    if (!whoami) return undefined;

    // Don't initialize until JWT is ready (when required)
    if (!isJwtReady) return undefined;

    const cleanupFns: Array<() => void> = [];

    const src = WX_COPILOT_URL;

    // Check if the script is already added to avoid duplicates
    const scripts = Array.from(document.getElementsByTagName('script'));
    const alreadyAdded = scripts.some(
      s =>
        s.id === 'wx-copilot-umd' ||
        s.getAttribute('data-wx-copilot-src') === src ||
        s.src === src,
    );

    if (!alreadyAdded) {
      const script = document.createElement('script');
      script.id = 'wx-copilot-umd';
      script.setAttribute('data-wx-copilot-src', src);
      script.src = src;
      script.async = true;
      document.body.appendChild(script);
    }

    // Determine the mount element
    let createdMount = false;
    let mountEl: HTMLElement | null =
      mountRef?.current ?? document.getElementById(mountId);
    if (!mountEl) {
      mountEl = document.createElement('div');
      mountEl.id = mountId;
      document.body.appendChild(mountEl);
      createdMount = true;
    }

    // Function to configure WxCopilot and then render
    const configureAndRender = () => {
      // Configure the component
      if (window.WxCopilotComponent?.setConfig) {
        try {
          window.WxCopilotComponent.setConfig({
            fountainAiBaseUrl: whoami?.fountain_ai_base_url,
            wxServiceBaseUrl: whoami?.account_wx_api_url,
            stage: domainMeta.environment,
            employerDomain: whoami?.account_wx_domain,
            employerBasePath: '',
            launchDarklyClientSideId:
              WX_LAUNCH_DARKLY_CLIENT_SIDE_ID || '652f17e244cb56124fa8cf95',
          });
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error('WxCopilotComponent.setConfig failed', e);
        }
      }

      // Render the component
      if (window.WxCopilotComponent?.render) {
        try {
          window.WxCopilotComponent.render(mountEl.id, renderProps);
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error('WxCopilotComponent.render failed', e);
        }
      }
    };

    // If already loaded, configure and render immediately
    if (window.WxCopilotComponent?.render) {
      configureAndRender();
    } else {
      // Wait for load event or poll
      const onLoaded = () => configureAndRender();
      window.addEventListener('WxCopilotComponent:loaded', onLoaded);

      const poll = setInterval(() => {
        if (window.WxCopilotComponent?.render) {
          clearInterval(poll);
          configureAndRender();
        }
      }, 200);

      // Add cleanup functions for async loading
      cleanupFns.push(() => clearInterval(poll));
      cleanupFns.push(() =>
        window.removeEventListener('WxCopilotComponent:loaded', onLoaded),
      );
    }

    // Add cleanup function for component destruction
    cleanupFns.push(() => {
      try {
        const id = mountEl?.id ?? mountId;
        window.WxCopilotComponentInstances?.[id]?.destroy?.();
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('WxCopilot destroy failed', e);
      }
    });

    // Add cleanup function for mount element removal (only if we created it)
    if (createdMount) {
      cleanupFns.push(() => {
        const el = document.getElementById(mountId);
        if (el?.parentNode) el.parentNode.removeChild(el);
      });
    }

    // Cleanup on unmount
    return () => {
      cleanupFns.forEach(fn => fn());
    };
  }, [mountRef, mountId, renderProps, whoami, isJwtReady]);
}
